/**
 * El canal de notificaciones en tiempo real — tarea 7.11
 *
 * SE LEE CON `fetch` Y NO CON `EventSource`. `EventSource` no puede mandar
 * cabeceras, y la sesión va en un `Authorization: Bearer`. Las dos salidas
 * habituales son malas: el token en la URL queda escrito en los registros de
 * nginx y en el historial del navegador, y la cookie de refresco está acotada a
 * `/api/v1/auth` a propósito.
 *
 * EL PRECIO ES QUE HAY QUE ESCRIBIR LA RECONEXIÓN, que `EventSource` daría
 * gratis. Es la parte delicada: una conexión que se cae y no vuelve deja la
 * campana muda sin dar ningún error, y nadie se entera hasta que echa en falta
 * un aviso.
 *
 * Por eso el reintento:
 *
 *  · Es exponencial con tope, para no martillear un servidor que está caído.
 *  · Se reinicia al conectar, para que un corte largo seguido de otro corto no
 *    herede la espera del anterior.
 *  · Se corta SIEMPRE la conexión anterior antes de abrir otra, y se cancela
 *    el cuerpo de las respuestas que no interesan. Sin eso, cada reintento deja
 *    una conexión viva: se midieron diecisiete desde una sola pestaña, y como
 *    el navegador limita a seis por servidor, el resto de peticiones se
 *    quedaban colgadas para siempre. El síntoma —la pestaña congelada mientras
 *    la API responde bien con `curl`— no señala aquí por ninguna parte.
 */

import { refreshOnce } from '@/lib/api-client';

export interface NotificacionEmpujada {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAt: string;
}

export interface OpcionesDelCanal {
  /** Cómo conseguir el token en cada intento. Se pide FRESCO: puede haberse renovado. */
  token: () => string | null;
  onNotificacion: (n: NotificacionEmpujada) => void;
  onEstado?: (conectado: boolean) => void;
}

const ESPERA_INICIAL = 1_000;
const ESPERA_MAXIMA = 30_000;

/**
 * Abre el canal y lo mantiene abierto. Devuelve la función para cerrarlo.
 */
export function escucharNotificaciones(opciones: OpcionesDelCanal): () => void {
  let cerrado = false;
  let espera = ESPERA_INICIAL;
  let controlador: AbortController | null = null;
  let temporizador: ReturnType<typeof setTimeout> | null = null;
  let conectando = false;

  const reintentar = () => {
    if (cerrado) return;
    if (temporizador) clearTimeout(temporizador);
    temporizador = setTimeout(() => void conectar(), espera);
    espera = Math.min(espera * 2, ESPERA_MAXIMA);
  };

  const conectar = async (): Promise<void> => {
    if (cerrado) return;

    // ── UNA SOLA CONEXIÓN A LA VEZ ────────────────────────────────────────
    //
    // Sin este cerrojo y sin abortar la anterior, cada reintento deja abierta
    // la conexión previa. Se midieron DIECISIETE conexiones vivas contra la API
    // desde una sola pestaña, y como el navegador limita a seis por servidor,
    // el resto de peticiones —incluida la propia lista de notificaciones— se
    // quedaban colgadas para siempre.
    //
    // El síntoma no señalaba aquí: la campana no se actualizaba y la pestaña
    // parecía congelada, mientras la API respondía perfectamente con `curl`.
    if (conectando) return;
    conectando = true;

    // La anterior se corta ANTES de abrir otra, pase lo que pase.
    controlador?.abort();

    const token = opciones.token();
    if (!token) {
      // Todavía no hay sesión: se espera y se vuelve a mirar.
      conectando = false;
      reintentar();
      return;
    }

    const propio = new AbortController();
    controlador = propio;

    try {
      const res = await fetch('/api/v1/notifications/stream', {
        headers: { Authorization: `Bearer ${token}` },
        signal: propio.signal,
      });

      // ── UN 401 SE ARREGLA RENOVANDO LA SESIÓN, NO REINTENTANDO A CIEGAS ───
      //
      // El token de acceso dura 15 minutos y vive en memoria. Cualquier otra
      // petición lo renueva sola cuando caduca, pero este canal es la ÚNICA
      // actividad de una pestaña que se deja abierta, y reintentaba cada 30 s
      // con el mismo token caducado, para siempre (visto en los registros de
      // producción: un 401 cada 30 segundos durante horas).
      if (res.status === 401) {
        await res.body?.cancel().catch(() => undefined);
        const renovado = await refreshOnce();
        conectando = false;
        if (renovado === 'ok') {
          espera = ESPERA_INICIAL;
          reintentar();
        } else if (renovado === 'rechazado') {
          // La sesión ya no vale: no hay nada que escuchar. Al volver a entrar,
          // la campana se monta de nuevo y abre su propio canal.
          opciones.onEstado?.(false);
          cerrado = true;
        } else {
          opciones.onEstado?.(false);
          reintentar();
        }
        return;
      }

      if (!res.ok || !res.body) {
        // SE CANCELA EL CUERPO. Una respuesta que no se lee mantiene la
        // conexión ocupada aunque no interese su contenido: es la mitad de la
        // fuga que se describe arriba. Con un 401 tampoco hay nada que
        // reintentar de inmediato —hace falta que la sesión se renueve—, pero
        // la espera exponencial ya se encarga.
        await res.body?.cancel().catch(() => undefined);
        opciones.onEstado?.(false);
        conectando = false;
        reintentar();
        return;
      }

      opciones.onEstado?.(true);
      espera = ESPERA_INICIAL;
      // Ya está establecida: el cerrojo se suelta para que un corte futuro
      // pueda reconectar. Lo que impide el solapamiento a partir de aquí es el
      // `abort` de la anterior, no este indicador.
      conectando = false;

      const lector = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let pendiente = '';

      for (;;) {
        const { value, done } = await lector.read();
        if (done) break;

        pendiente += value;

        // Los eventos van separados por una línea en blanco. Se procesa lo que
        // esté COMPLETO y se guarda el resto: un evento puede llegar partido en
        // dos trozos y montarlo a medias daría un JSON inválido.
        const trozos = pendiente.split('\n\n');
        pendiente = trozos.pop() ?? '';

        for (const trozo of trozos) {
          // Los comentarios (`: latido`) se ignoran, que es lo que dice SSE.
          const evento = /^event: (.+)$/m.exec(trozo)?.[1];
          const datos = /^data: (.+)$/m.exec(trozo)?.[1];

          if (evento === 'notificacion' && datos) {
            try {
              opciones.onNotificacion(JSON.parse(datos) as NotificacionEmpujada);
            } catch {
              /* un evento ilegible no puede tumbar el canal */
            }
          }
        }
      }

      // El servidor ha cerrado. No es un error: se vuelve a abrir.
      opciones.onEstado?.(false);
      conectando = false;
      reintentar();
    } catch {
      // `abort` al cerrar nosotros entra por aquí; `cerrado` lo distingue.
      opciones.onEstado?.(false);
      conectando = false;
      reintentar();
    }
  };

  void conectar();

  return () => {
    cerrado = true;
    if (temporizador) clearTimeout(temporizador);
    controlador?.abort();
  };
}
